import { Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { useContext } from 'react'
import { toast } from 'react-hot-toast'
import { ClientContext } from 'src/components/providers/ClientProvider'
import type { ProSubscriptionStatus, ProSubscriptionStatusValue } from 'src/types/proSubscription'
import { useHttpsCallable } from 'src/utils/firebase'
import { capture } from 'src/utils/sentry'

type Props = {
  clientId: string
}

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })

const intervalLabel = (interval: ProSubscriptionStatus['interval']) => {
  switch (interval) {
    case 'monthly':
      return '月額'
    case 'yearly':
      return '年額'
  }
}

const collectionMethodLabel = (method: ProSubscriptionStatus['collectionMethod']) => {
  switch (method) {
    case 'charge_automatically':
      return 'カード'
    case 'send_invoice':
      return '銀行振込'
  }
}

const statusLabel = (status: ProSubscriptionStatusValue) => {
  switch (status) {
    case 'active':
      return 'アクティブ'
    case 'trialing':
      return 'トライアル中'
    case 'past_due':
      return '支払い遅延'
    case 'unpaid':
      return '未払い'
    case 'paused':
      return '一時停止'
    case 'incomplete':
      return '支払い未完了'
    case 'incomplete_expired':
      return '支払い未完了 (期限切れ)'
    case 'canceled':
      return 'キャンセル済み'
    default:
      return status
  }
}

const isWarningStatus = (status: ProSubscriptionStatusValue) => {
  switch (status) {
    case 'active':
    case 'trialing':
      return false
    default:
      return true
  }
}

export const ProSubscriptionPanel = ({ clientId }: Props) => {
  const { proSubscription, isProSubscriptionLoading, refreshProSubscription } = useContext(ClientContext)
  const [callPortal, isCallingPortal] = useHttpsCallable<{ clientId: string }, { url: string }>('createBillingPortalSession')
  const [callCancelPending, isCancelingPending] = useHttpsCallable<{ clientId: string }, { ok: boolean }>('cancelPendingProSubscription')
  const [callCheckout, isCallingCheckout] = useHttpsCallable<{ clientId: string; interval: 'monthly' | 'yearly' }, { url: string }>('createProCheckoutSession')

  const handleManage = async () => {
    try {
      const res = await callPortal({ clientId })
      if (res?.data?.url) {
        window.location.href = res.data.url
      } else {
        toast.error('ポータルの URL の取得に失敗しました')
      }
    } catch (e) {
      capture(e)
      toast.error('Pro 管理ポータルを開けませんでした')
    }
  }

  const handleSwitchToCard = async (interval: 'monthly' | 'yearly') => {
    if (!confirm('現在の申込みをキャンセルして、カード払いで再申込みしますか？\n発行済みの請求書は無効になります。')) return
    try {
      await callCancelPending({ clientId })
      const res = await callCheckout({ clientId, interval })
      if (res?.data?.url) {
        window.location.href = res.data.url
      } else {
        toast.error('カード決済ページの URL を取得できませんでした')
      }
    } catch (e) {
      capture(e)
      toast.error('カード払いへの切替に失敗しました')
    }
  }

  const handleCancelPending = async () => {
    if (
      !confirm(
        'Pro プランの申込みを取り消しますか？\n発行済みの請求書は無効になります。\n取消後、期間 (月額 / 年額) や支払い方法を選び直して再申込みできます。',
      )
    )
      return
    try {
      await callCancelPending({ clientId })
      toast.success('申込みを取り消しました')
      await refreshProSubscription()
    } catch (e) {
      capture(e)
      toast.error('申込みの取消に失敗しました')
    }
  }

  if (isProSubscriptionLoading) {
    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Skeleton variant="text" width={180} />
          <Skeleton variant="text" width={220} />
          <Skeleton variant="text" width={140} />
        </Stack>
      </Paper>
    )
  }

  if (!proSubscription) {
    return (
      <Paper elevation={3} sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Pro プランの情報を取得できませんでした
          </Typography>
          <Button variant="outlined" size="small" disabled={isCallingPortal} onClick={handleManage}>
            プランを管理
          </Button>
        </Stack>
      </Paper>
    )
  }

  const periodEndLabel = proSubscription.cancelAtPeriodEnd || proSubscription.status === 'canceled' ? '利用可能期限' : '次回更新日'
  const autoRenewLabel = (() => {
    if (proSubscription.collectionMethod === 'charge_automatically') {
      return proSubscription.cancelAtPeriodEnd ? 'OFF (期間末でキャンセル予定)' : 'ON (カードから自動引き落とし)'
    }
    return 'OFF (期ごとに請求書を発行)'
  })()

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
            }}
          >
            Pro プラン
          </Typography>
          <Chip size="small" label={`${intervalLabel(proSubscription.interval)} / ${collectionMethodLabel(proSubscription.collectionMethod)}`} />
          {!proSubscription.isEffectivelyPro && <Chip size="small" color="warning" label="お支払い待ち" />}
          {proSubscription.isEffectivelyPro && isWarningStatus(proSubscription.status) && (
            <Chip size="small" color="warning" label={statusLabel(proSubscription.status)} />
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {periodEndLabel}
            </Typography>
            <Typography variant="body1">{formatDate(proSubscription.currentPeriodEnd)}</Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              自動更新
            </Typography>
            <Typography variant="body1">{autoRenewLabel}</Typography>
          </Box>
        </Stack>

        {proSubscription.pendingInvoice && (
          <Box sx={{ p: 1.5, backgroundColor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              未払いの請求書があります
            </Typography>
            {proSubscription.pendingInvoice.dueDate && <Typography variant="body2">振込期限: {formatDate(proSubscription.pendingInvoice.dueDate)}</Typography>}
            {proSubscription.pendingInvoice.hostedUrl && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                href={proSubscription.pendingInvoice.hostedUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 1 }}
              >
                請求書 / 振込先を見る
              </Button>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {proSubscription.isEffectivelyPro ? (
            <Button variant="outlined" size="small" disabled={isCallingPortal} onClick={handleManage}>
              プランを管理 / 解約
            </Button>
          ) : (
            <>
              {proSubscription.collectionMethod === 'send_invoice' && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isCancelingPending || isCallingCheckout}
                  onClick={() => handleSwitchToCard(proSubscription.interval)}
                >
                  カード払いに切り替える
                </Button>
              )}
              <Button variant="outlined" size="small" color="error" disabled={isCancelingPending} onClick={handleCancelPending}>
                申込みを取り消す
              </Button>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  )
}
